import { useState } from 'react';
import { Phone, Mail, Globe, MapPin, Wrench, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import EnquiryForm from './EnquiryForm';
import { resolveLogoUrl, trackTradieEvent } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';

const TradieCard = ({ tradie }) => {
  const logoSrc = resolveLogoUrl(tradie.logo_url);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  // Map UI click "method" -> backend event_type for in-app analytics tab
  const METHOD_TO_EVENT = {
    phone: 'phone_click',
    email: 'email_click',
    website: 'website_click',
    enquiry_open: 'enquiry_open',
  };

  const trackContact = (method) => {
    // Google Analytics 4
    trackEvent('tradie_contact_click', {
      method,
      tradie_id: tradie.id,
      tradie_name: tradie.business_name,
    });
    // In-app DB tracking (so Admin Dashboard Analytics can show real numbers)
    const eventType = METHOD_TO_EVENT[method];
    if (eventType) trackTradieEvent(tradie.id, eventType);
  };

  const openDetails = () => {
    trackEvent('tradie_card_expand', {
      tradie_id: tradie.id,
      tradie_name: tradie.business_name,
    });
    trackTradieEvent(tradie.id, 'card_expand');
    setDetailsOpen(true);
  };

  const openEnquiryFromDetails = () => {
    trackContact('enquiry_open');
    setDetailsOpen(false);
    // small delay to let the details dialog close cleanly before the next one opens
    setTimeout(() => setEnquiryOpen(true), 80);
  };

  const websiteHref = tradie.website
    ? (tradie.website.startsWith('http') ? tradie.website : `https://${tradie.website}`)
    : null;

  return (
    <>
      <div
        className="bg-white border border-stone-200 rounded-xl p-6 shadow-sm hover:shadow-lg hover:border-primary/30 transition-all duration-300 hover:-translate-y-1 flex flex-col"
        data-testid={`tradie-card-${tradie.id}`}
      >
        {/* Header — clickable */}
        <button
          type="button"
          onClick={openDetails}
          className="flex items-start gap-4 mb-4 text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-md"
          aria-label={`View full details for ${tradie.business_name}`}
          data-testid={`tradie-card-expand-${tradie.id}`}
        >
          {tradie.logo_url ? (
            <img
              src={logoSrc}
              alt={`${tradie.business_name} logo`}
              className="w-16 h-16 rounded-lg object-contain bg-white border border-stone-200 flex-shrink-0"
              data-testid={`tradie-logo-${tradie.id}`}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <div className="w-16 h-16 rounded-lg bg-primary/10 border border-stone-200 flex items-center justify-center flex-shrink-0" aria-hidden="true">
              <Wrench className="w-7 h-7 text-primary" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-heading font-semibold text-lg text-foreground truncate hover:text-primary transition-colors">
                {tradie.business_name}
              </h3>
              {tradie.subscription_status === 'active' && (
                <CheckCircle className="w-4 h-4 text-secondary flex-shrink-0" />
              )}
            </div>
            <p className="text-sm text-muted-foreground">{tradie.contact_name}</p>
          </div>
        </button>

        {/* Description — clickable, with Read more */}
        <button
          type="button"
          onClick={openDetails}
          className="text-left mb-4 group focus:outline-none"
          data-testid={`tradie-description-expand-${tradie.id}`}
        >
          <p className="text-sm text-stone-600 line-clamp-3">
            {tradie.description}
          </p>
          <span className="text-xs font-medium text-primary mt-1 inline-block group-hover:underline">
            View full details →
          </span>
        </button>

        {/* Categories */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {tradie.categories.slice(0, 3).map((category) => (
            <Badge
              key={category}
              variant="secondary"
              className="bg-primary/10 text-primary border-0 text-xs"
            >
              <Wrench className="w-3 h-3 mr-1" />
              {category}
            </Badge>
          ))}
          {tradie.categories.length > 3 && (
            <Badge variant="outline" className="text-xs">
              +{tradie.categories.length - 3} more
            </Badge>
          )}
        </div>

        {/* Regions */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {tradie.regions.slice(0, 2).map((region) => (
            <Badge
              key={region}
              variant="outline"
              className="text-xs text-muted-foreground"
            >
              <MapPin className="w-3 h-3 mr-1" />
              {region}
            </Badge>
          ))}
          {tradie.regions.length > 2 && (
            <Badge variant="outline" className="text-xs text-muted-foreground">
              +{tradie.regions.length - 2} regions
            </Badge>
          )}
        </div>

        {/* Contact Info */}
        <div className="space-y-2 mb-4 mt-auto border-t border-stone-100 pt-4">
          <a
            href={`tel:${tradie.phone}`}
            className="flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors"
            data-testid={`tradie-phone-${tradie.id}`}
            onClick={() => trackContact('phone')}
          >
            <Phone className="w-4 h-4 text-primary" />
            {tradie.phone}
          </a>
          <a
            href={`mailto:${tradie.email}`}
            className="flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors"
            data-testid={`tradie-email-${tradie.id}`}
            onClick={() => trackContact('email')}
          >
            <Mail className="w-4 h-4 text-primary" />
            {tradie.email}
          </a>
          {websiteHref && (
            <a
              href={websiteHref}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors"
              data-testid={`tradie-website-${tradie.id}`}
              onClick={() => trackContact('website')}
            >
              <Globe className="w-4 h-4 text-primary" />
              Website
            </a>
          )}
        </div>

        {/* Action Button */}
        <Button
          className="w-full bg-primary hover:bg-primary/90"
          data-testid={`enquiry-btn-${tradie.id}`}
          onClick={() => { trackContact('enquiry_open'); setEnquiryOpen(true); }}
        >
          Send Enquiry
        </Button>
      </div>

      {/* Full Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-stone-100">
            <div className="flex items-start gap-4">
              {tradie.logo_url ? (
                <img
                  src={logoSrc}
                  alt={`${tradie.business_name} logo`}
                  className="w-20 h-20 rounded-lg object-contain bg-white border border-stone-200 flex-shrink-0"
                />
              ) : (
                <div className="w-20 h-20 rounded-lg bg-primary/10 border border-stone-200 flex items-center justify-center flex-shrink-0">
                  <Wrench className="w-9 h-9 text-primary" />
                </div>
              )}
              <div className="flex-1 min-w-0 text-left">
                <DialogTitle className="font-heading text-2xl flex items-center gap-2">
                  {tradie.business_name}
                  {tradie.subscription_status === 'active' && (
                    <CheckCircle className="w-5 h-5 text-secondary" />
                  )}
                </DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground mt-1">
                  {tradie.contact_name}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh] px-6 py-5">
            {/* Full description */}
            <div className="mb-6">
              <h4 className="font-heading text-sm font-semibold text-foreground mb-2 uppercase tracking-wide">About</h4>
              <p className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">
                {tradie.description}
              </p>
            </div>

            {/* All Categories */}
            <div className="mb-6">
              <h4 className="font-heading text-sm font-semibold text-foreground mb-2 uppercase tracking-wide flex items-center gap-2">
                <Wrench className="w-4 h-4 text-primary" /> Services
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {tradie.categories.map((category) => (
                  <Badge
                    key={category}
                    variant="secondary"
                    className="bg-primary/10 text-primary border-0 text-xs"
                  >
                    {category}
                  </Badge>
                ))}
              </div>
            </div>

            {/* All Regions */}
            <div className="mb-6">
              <h4 className="font-heading text-sm font-semibold text-foreground mb-2 uppercase tracking-wide flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" /> Regions Served
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {tradie.regions.map((region) => (
                  <Badge key={region} variant="outline" className="text-xs text-muted-foreground">
                    {region}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div className="border-t border-stone-100 pt-4 mb-2">
              <h4 className="font-heading text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">Contact</h4>
              <div className="space-y-2">
                <a
                  href={`tel:${tradie.phone}`}
                  className="flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors"
                  onClick={() => trackContact('phone')}
                >
                  <Phone className="w-4 h-4 text-primary" />
                  {tradie.phone}
                </a>
                <a
                  href={`mailto:${tradie.email}`}
                  className="flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors"
                  onClick={() => trackContact('email')}
                >
                  <Mail className="w-4 h-4 text-primary" />
                  {tradie.email}
                </a>
                {websiteHref && (
                  <a
                    href={websiteHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors"
                    onClick={() => trackContact('website')}
                  >
                    <Globe className="w-4 h-4 text-primary" />
                    {tradie.website}
                  </a>
                )}
              </div>
            </div>
          </ScrollArea>

          <div className="px-6 py-4 border-t border-stone-100 bg-stone-50">
            <Button
              className="w-full bg-primary hover:bg-primary/90"
              onClick={openEnquiryFromDetails}
              data-testid={`details-enquiry-btn-${tradie.id}`}
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Enquiry
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Enquiry Dialog */}
      <Dialog open={enquiryOpen} onOpenChange={setEnquiryOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Contact {tradie.business_name}
            </DialogTitle>
          </DialogHeader>
          <EnquiryForm tradieId={tradie.id} tradieName={tradie.business_name} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TradieCard;
